import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { translateSupabaseError } from "@/utils/errorTranslation";
import type { Quotation, QuotationWithDetails, QuotationFilters } from "@/types/quotation";
import { useMultiTenant } from "./useMultiTenant";
import { useState } from "react";
import { buildDiff, AUDITABLE_QUOTATION_FIELDS } from "@/utils/auditHelpers";

export function useQuotations(filters?: QuotationFilters) {
  const queryClient = useQueryClient();
  const { companyId } = useMultiTenant();
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });

  const query = useQuery({
    queryKey: ["quotations", companyId, filters, pagination],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID não disponível");

      const { data, error } = await supabase.rpc("get_quotations_filtered", {
        p_company_id: companyId,
        p_search: filters?.searchTerm || null,
        p_status: filters?.status || null,
        p_customer_id: filters?.customerId || null,
        p_start_date: filters?.startDate?.toISOString().split('T')[0] || null,
        p_end_date: filters?.endDate?.toISOString().split('T')[0] || null,
        p_number_from: filters?.numberFrom || null,
        p_number_to: filters?.numberTo || null,
        p_limit: pagination.pageSize,
        p_offset: (pagination.page - 1) * pagination.pageSize,
      });

      if (error) throw error;

      return data?.map((q: any) => ({
        ...q,
        customer: {
          id: q.customer_id,
          name: q.customer_name,
          document_number: q.customer_document,
          email: q.customer_email,
          phone: q.customer_phone,
          city: q.customer_city,
          state: q.customer_state,
        },
        items: q.items || [],
      })) || [];
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const statsQuery = useQuery({
    queryKey: ["quotations-stats", companyId, filters?.startDate, filters?.endDate],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID não disponível");

      const { data, error } = await supabase.rpc("get_quotations_stats", {
        p_company_id: companyId,
        p_start_date: filters?.startDate?.toISOString().split('T')[0] || null,
        p_end_date: filters?.endDate?.toISOString().split('T')[0] || null,
      });

      if (error) throw error;
      return data?.[0] || {};
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const createQuotation = useMutation({
    // silentSuccess: suprime o toast de sucesso do onSuccess — para fluxos em
    // que o componente exibe um toast contextual próprio (ex.: UnifiedOrderForm
    // "Orçamento salvo! Agora você pode gerar a venda."). Erros disparam sempre.
    mutationFn: async (quotation: Omit<Quotation, 'id' | 'quotation_number' | 'created_at' | 'updated_at' | 'deleted_at' | 'deleted_by'> & { items: any[]; custom_number?: number; silentSuccess?: boolean }) => {
      if (!companyId) throw new Error("Company ID não disponível");
      
      const { items, custom_number, silentSuccess: _silentSuccess, ...quotationData } = quotation;

      // quotation_number is now generated atomically by BEFORE INSERT trigger
      const quotationNumberOverride = custom_number
        ? String(custom_number).padStart(6, '0')
        : undefined;

      const { data: { user } } = await supabase.auth.getUser();
      const { data: newQuotation, error: quotationError } = await supabase
        .from("quotations")
        .insert([{ 
          ...quotationData, 
          ...(quotationNumberOverride ? { quotation_number: quotationNumberOverride } : {}),
          created_by: user?.id
        } as any])
        .select()
        .single();

      if (quotationError) throw quotationError;

      // Create items
      const itemsWithQuotationId = items.map((item) => ({
        ...item,
        quotation_id: newQuotation.id,
        company_id: quotationData.company_id,
      }));

      const { error: itemsError } = await supabase
        .from("quotation_items")
        .insert(itemsWithQuotationId);

      if (itemsError) throw itemsError;

      return newQuotation;
    },
    onSuccess: (newQuotation, variables) => {
      queryClient.invalidateQueries({ queryKey: ["quotations"] });
      queryClient.invalidateQueries({ queryKey: ["quotations-stats"] });
      queryClient.invalidateQueries({ queryKey: ["unified-sales"] });
      queryClient.invalidateQueries({ queryKey: ["unified-sales-stats"] });

      // Gravar criação de orçamento em audit_logs — fire-and-forget, nunca bloqueante
      supabase.rpc('log_sales_event', {
        p_action_type: 'CRIAÇÃO',
        p_entity_type: 'orçamento',
        p_entity_id:   newQuotation.id,
        p_details: {
          event:            'quotation_created',
          quotation_number: newQuotation.quotation_number,
          customer_id:      newQuotation.customer_id,
          total_value:      newQuotation.total_value,
          status:           newQuotation.status,
        },
      }).then(({ error }) => {
        if (error) console.warn('[audit] log_sales_event quotation_created falhou:', error.message);
      }).catch((err) => {
        console.warn('[audit] log_sales_event quotation_created erro inesperado:', err);
      });

      if (!variables.silentSuccess) {
        toast({
          title: "Orçamento criado",
          description: "O orçamento foi criado com sucesso.",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar orçamento",
        description: translateSupabaseError(error),
        variant: "destructive",
      });
    },
  });

  const updateQuotation = useMutation({
    mutationFn: async ({
      id,
      data,
      items,
    }: {
      id: string;
      data: Partial<Quotation>;
      items?: any[];
    }) => {
      // Capturar estado anterior para diff de auditoria
      const { data: beforeSnapshot } = await supabase
        .from("quotations")
        .select("quotation_number, total_value, status, discount_percentage, discount_value, notes, valid_until, salesperson_id")
        .eq("id", id)
        .maybeSingle();

      const { error: quotationError } = await supabase
        .from("quotations")
        .update(data)
        .eq("id", id);

      if (quotationError) throw quotationError;

      if (items) {
        // Delete existing items
        const { error: deleteError } = await supabase
          .from("quotation_items")
          .delete()
          .eq("quotation_id", id);

        if (deleteError) {
          console.error("Erro ao deletar items:", deleteError);
          throw new Error(`Erro ao deletar items antigos: ${deleteError.message}`);
        }

        // Insert new items
        const itemsWithQuotationId = items.map((item) => ({
          ...item,
          quotation_id: id,
          company_id: companyId,
        }));

        const { error: itemsError } = await supabase
          .from("quotation_items")
          .insert(itemsWithQuotationId);

        if (itemsError) throw itemsError;
      }

      return { id, before: beforeSnapshot as Record<string, unknown> | null, afterData: data };
    },
    onSuccess: async (result) => {
      const { id, before } = result;
      queryClient.invalidateQueries({ queryKey: ["quotations"] });
      queryClient.invalidateQueries({ queryKey: ["quotations-stats"] });
      queryClient.invalidateQueries({ queryKey: ["unified-sales"] });
      queryClient.invalidateQueries({ queryKey: ["unified-sales-stats"] });

      // Buscar estado pós-UPDATE para diff granular
      const { data: afterSnapshot } = await supabase
        .from("quotations")
        .select("quotation_number, total_value, status, discount_percentage, discount_value, notes, valid_until, salesperson_id")
        .eq("id", id)
        .maybeSingle();

      const changes = (before && afterSnapshot)
        ? buildDiff(before, afterSnapshot as Record<string, unknown>, AUDITABLE_QUOTATION_FIELDS)
        : [];

      // Fire-and-forget — nunca bloqueante, nunca silencioso
      supabase.rpc('log_sales_event', {
        p_action_type: 'ATUALIZAÇÃO',
        p_entity_type: 'orçamento',
        p_entity_id:   id,
        p_details: {
          event:            'quotation_updated',
          quotation_number: afterSnapshot?.quotation_number ?? before?.quotation_number,
          total_value:      afterSnapshot?.total_value,
          status:           afterSnapshot?.status,
          changes,
        },
      }).then(({ error }) => {
        if (error) console.warn('[audit] log_sales_event quotation_updated falhou:', error.message);
      }).catch((err) => {
        console.warn('[audit] log_sales_event quotation_updated erro inesperado:', err);
      });

      toast({
        title: "Orçamento atualizado",
        description: "O orçamento foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar orçamento",
        description: translateSupabaseError(error),
        variant: "destructive",
      });
    },
  });

  const deleteQuotation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const { error } = await supabase.rpc("delete_quotation_with_audit", {
        p_quotation_id: id,
        p_reason: reason || "Exclusão manual",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotations"] });
      queryClient.invalidateQueries({ queryKey: ["quotations-stats"] });
      queryClient.invalidateQueries({ queryKey: ["unified-sales"] });
      queryClient.invalidateQueries({ queryKey: ["unified-sales-stats"] });
      toast({
        title: "Orçamento excluído",
        description: "O orçamento foi excluído com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao excluir orçamento",
        description: translateSupabaseError(error),
        variant: "destructive",
      });
    },
  });

  // ──────────────────────────────────────────────────────────────
  // convertToSale (removido em 22/04/2026 — §I.9 do Brain):
  //   O fluxo legado fazia INSERT direto em sales + UPDATE em quotations
  //   sem gate RBAC e sem atomicidade, criando janela para vendas órfãs
  //   e duplicação de numeração. A conversão agora passa exclusivamente
  //   pela Edge Function create-sale (com gate sales:create) chamando a
  //   RPC create_sale_atomic v3 com p_convert_from_quotation_id. Ver
  //   ConvertQuotationDialog.handleConfirmPayment e
  //   UnifiedOrderForm.handleConfirmPayment.
  // ──────────────────────────────────────────────────────────────

  return {
    quotations: query.data || [],
    totalCount: query.data?.[0]?.total_count || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading,
    isLoadingStats: statsQuery.isLoading,
    pagination,
    setPagination,
    createQuotation,
    updateQuotation,
    deleteQuotation,
  };
}
